<script lang="ts">
  import SkuItem from "@shared/svelte/Components/ProductLines/ProductListCard/SkuItem.svelte";
  import FulfillmentMethodBanner from "@retailer/svelte/Shared/FulfillmentMethodBanner.svelte";
  import {
    ProductLineListingStore,
    updateProductLineListingStore,
  } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import SkeletonProductListCardSkuItem from "@shared/svelte/Components/ProductLines/Skeletons/SkeletonProductListCardSkuItem.svelte";
  import {
    filterProductListSkuItems,
    sortProductListSkuItems,
  } from "@shared/services/product_list_sku_items_sorting";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { cubicInOut } from "svelte/easing";
  import { slide } from "svelte/transition";
  import { min } from "lodash";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  import {
    Inventory,
    Product,
    ProductLine,
    ProductListAccordionState,
  } from "@shared/types/ProductLine";
  import { onDestroy } from "svelte";

  const { trackClick } = EventTracking;

  export let listViewAccordionStatesKey: string | number;
  export let productLine: ProductLine;
  export let productInventoryCount: number;
  export let Cart;
  export let stateParams: any = {};
  export let productInventory: Inventory[];

  let productInventoryData: Product[];

  let discountDataReady: boolean = false;
  let futurePriceDataReady: boolean = false;
  let showMore: boolean = false;

  const productLineInventory = productLine?.products?.map(p => p.inventory).flat();

  const unsubscribe = ProductLineListingStore.subscribe(n => {
    showMore =
      n.listViewAccordionStates[listViewAccordionStatesKey] === ProductListAccordionState.Expanded;

    if (n.productData[productLine.id]?.length) {
      productInventoryData = filterProductListSkuItems(
        n.productData[productLine.id],
        productLineInventory,
      );
      productInventoryData = sortProductListSkuItems(productInventoryData);
      const newCount = productInventoryData?.length;
      if (productInventoryCount !== newCount) {
        productInventoryCount = newCount;
        productInventory = productInventoryData?.flatMap(p => p.inventory);
      }
    }

    if (!!n.discountData[productLine.id]) {
      discountDataReady = true;
    }
    if (
      !!n.productLineFuturePrices[productLine.id] ||
      Object.keys(n.productLineFuturePrices).length === 0
    ) {
      futurePriceDataReady = true;
    }
  });

  onDestroy(() => {
    unsubscribe();
  });

  const storeAccordionState = newStoreState => {
    updateProductLineListingStore(n => {
      n.listViewAccordionStates[listViewAccordionStatesKey] = newStoreState;
      return n;
    });
  };

  const handleShowMore = trackingParams => {
    trackClick(
      `${showMore ? "show_less" : "show_more"}_products_in_accordion`,
      trackingParams,
      false,
    );
    showMore = !showMore;

    if (!showMore) {
      document.getElementById(`product-list-card-${productLine.id}`).scrollIntoView({
        behavior: "smooth",
      });
    }

    storeAccordionState(
      showMore ? ProductListAccordionState.Expanded : ProductListAccordionState.Open,
    );
  };

  const usesFulfillmentMethodBanner =
    shouldUseRetailerFulfillmentMethodForDistributor(
      productLine?.distributor_info?.distributor_id,
      productLine?.distributor_info?.uses_warehouse_pickup,
      stagedRolloutCheck("marketplace__fulfillment_methods") &&
        productLine?.distributor_info?.uses_wine_cellar,
    ) && !hideFulfillmentMethodPickerForDistributor(productLine?.distributor_info?.distributor_id);

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );

  const needsAccountNumber = async () => {
    if (!wineCellarStagedRolloutEnabled)
      return productLine?.distributor_info?.requires_account_number;

    const sku = productLineInventory[0]?.sku;
    return await isAccountNumberRequired(
      productLine?.distributor_info?.distributor_id,
      sku,
      null,
      productLine?.distributor_info?.account_number,
    );
  };
</script>

<TrackingContext let:trackingContext>
  {#if usesFulfillmentMethodBanner}
    {#await needsAccountNumber() then needsAccountNumber}
      {#if !needsAccountNumber}
        <div class="fizz-inset-square-16">
          <FulfillmentMethodBanner
            distributor={productLine?.distributor_info}
            products={productInventoryData}
            {productLine}
            on:openDrawer
          />
        </div>
      {/if}
    {/await}
  {/if}
  <div
    class="fizz-inset-horizontal-16"
    transition:slide|local={{ duration: 300, easing: cubicInOut }}
  >
    {#if productInventoryData && productInventoryCount > 0}
      {#each Array(min([productInventoryCount, 5])) as _, index}
        {#if productInventoryData.length && discountDataReady && futurePriceDataReady}
          <SkuItem
            {productLine}
            bind:product={productInventoryData[index]}
            {productInventoryData}
            {index}
            {Cart}
            {stateParams}
            on:openDrawer
          />
        {:else}
          <SkeletonProductListCardSkuItem {index} />
        {/if}
      {/each}

      {#if productInventoryData}
        {#if showMore}
          <div transition:slide|local={{ duration: 300, easing: cubicInOut }}>
            <div class="fizz-stack-16 fizz-border-top" />
            {#each productInventoryData.slice(5) as product, index}
              <SkuItem
                {productLine}
                {product}
                {index}
                {Cart}
                {stateParams}
                {productInventoryData}
                on:openDrawer
              />
            {/each}
          </div>
        {/if}

        {#if productInventoryData.length && productInventoryCount > 5}
          <div
            class="fizz-flex-row fizz-border-top fizz-inset-vertical-16"
            data-testid="show-more-container"
          >
            <p class="fizz-text-subdued">
              Showing 1-{showMore ? productInventoryCount : 5} of {productInventoryCount}
            </p>
            <a
              href="#"
              on:click|preventDefault|stopPropagation={() => {
                handleShowMore(trackingContext.entities);
              }}
              data-testid="show-more-or-less-button"
            >
              Show {showMore ? "less" : "more"} items
            </a>
          </div>
        {/if}
      {/if}
    {/if}
  </div>
</TrackingContext>
